import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import { useDirtySections } from '@/components/profile';
import { useNotify, useToggle } from '@/hooks';
import { API } from '@/services/api.service';
import { COMPANY_FORM_GROUP_KEYS } from '@/shared/company';
import { QUERY_KEY } from '@/shared/constants';
import { useAuthStore } from '@/store/useAuthStore';
import { companySectionOfField } from '../companyProfileSections';
import { DOCS_SETTINGS_ROLES } from './constants';

/**
 * Form instance, profile-section helpers, field permissions and the client/officer/admin keys.
 */
export default function useCompanyFormBase({
  formData,
  formProp,
  isEdited,
  onDirtySectionsChange,
  profileSection,
  readOnly,
}) {
  const notification = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const companyParams = {
    filter: { 'applicationSettings.typeOfCompany': 'main' },
  };

  const { data: companyOptions, isLoading: companyIsLoading } = useQuery({
    queryKey: [QUERY_KEY.companiesDropdown, companyParams],
    queryFn: async () => {
      return await API.companies.retrieveDropdownAllCompanies(companyParams);
    },
    select: (data) => {
      const companies = data?.items || [];

      return companies?.map((company) => ({
        label: company.name,
        value: company._id,
      }));
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  // * SIDEBAR FORM
  const { open, setOpen, onClose } = useToggle(false);

  // * HISTORY SIDEBAR
  const {
    open: historyOpen,
    setOpen: setHistoryOpen,
    onClose: onHistoryClose,
  } = useToggle(false);

  // * DOCS SETTINGS SIDEBAR
  const {
    open: docsSettingsOpen,
    setOpen: setDocsSettingsOpen,
    onClose: onDocsSettingsClose,
  } = useToggle(false);

  const currentUserRoles = useAuthStore((state) => state.user?.roles) || [];
  const canManageDocsSettings = DOCS_SETTINGS_ROLES.some((role) =>
    currentUserRoles.includes(role)
  );

  const { t } = useTranslation([
    'forms',
    'messages',
    'menus',
    'tables',
    'translations',
  ]);
  const [ownForm] = Form.useForm();
  const form = formProp || ownForm;
  const isProfile = !!profileSection;
  // Field / block visibility on the profile page; always visible elsewhere.
  const inSection = (id) => !isProfile || profileSection === id;
  const sectionClass = (id) => (inSection(id) ? '' : 'hidden');
  // Form values as loaded; on the profile page Save sends only what differs.
  const baselineRef = useRef(null);

  useDirtySections({
    form,
    baselineRef,
    enabled: isProfile,
    sectionOfField: companySectionOfField,
    onChange: onDirtySectionsChange,
  });

  const formFields = formData?.COMPANY_GENERAL_DATA_FORM_FIELDS;

  const getFieldState = (keys) => {
    const path = keys.join('.');
    const field = formFields?.[path] || {};

    return {
      visible: isEdited ? field.visibility : true,
      // Read-only profile view: every field locked, whatever the server allows.
      disabled: readOnly || (isEdited ? field.disabled : false),
    };
  };

  const INPUT_KEYS = COMPANY_FORM_GROUP_KEYS;

  const clientsAdminsClientsKeys = INPUT_KEYS.clientsAdminsClients;
  const clientsAdminsClientState = getFieldState(
    clientsAdminsClientsKeys.split('.')
  );

  const clientsAdminsClientOfficersKeys =
    INPUT_KEYS.clientsAdminsClientOfficers;
  const clientsAdminsClientOfficersState = getFieldState(
    clientsAdminsClientOfficersKeys.split('.')
  );

  const clientsAdminsEInvoiceOfficersKeys =
    INPUT_KEYS.clientsAdminsEInvoiceOfficers;
  const clientsAdminsEInvoiceOfficersState = getFieldState(
    clientsAdminsEInvoiceOfficersKeys.split('.')
  );

  const clientsAdminsCompanyAdminsKeys = INPUT_KEYS.clientsAdminsCompanyAdmins;
  const clientsAdminsCompanyAdminsState = getFieldState(
    clientsAdminsCompanyAdminsKeys.split('.')
  );

  return {
    INPUT_KEYS,
    baselineRef,
    canManageDocsSettings,
    clientsAdminsClientOfficersKeys,
    clientsAdminsClientOfficersState,
    clientsAdminsClientState,
    clientsAdminsClientsKeys,
    clientsAdminsCompanyAdminsKeys,
    clientsAdminsCompanyAdminsState,
    clientsAdminsEInvoiceOfficersKeys,
    clientsAdminsEInvoiceOfficersState,
    companyIsLoading,
    companyOptions,
    docsSettingsOpen,
    form,
    getFieldState,
    historyOpen,
    inSection,
    isProfile,
    navigate,
    notification,
    onClose,
    onDocsSettingsClose,
    onHistoryClose,
    open,
    queryClient,
    sectionClass,
    setDocsSettingsOpen,
    setHistoryOpen,
    setOpen,
    t,
  };
}
